import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Shield, AlertTriangle, Eye, CheckCircle2, TrendingUp, Clock, HelpCircle, ChevronDown, FileSearch } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const keyMetrics = [
  {
    icon: Clock,
    value: '8 Weeks',
    label: 'Typical Timeline',
    description: 'Full enterprise platform assessment timeline'
  },
  {
    icon: TrendingUp,
    value: '88',
    label: 'Services Assessed',
    description: 'Maximum microservice estate assessed in a single engagement'
  },
  {
    icon: CheckCircle2,
    value: '100%',
    label: 'Evidence-Based',
    description: 'Every finding backed by code, data, or architectural evidence'
  },
  {
    icon: Shield,
    value: 'Zero',
    label: 'Assumptions',
    description: 'We confirm before we report — no speculative risk ratings'
  }
];

const services = [
  {
    title: 'Application Architecture Audit',
    icon: Search,
    items: [
      'Microservices Mapping: Complete inventory and dependency mapping of distributed service architectures',
      'Service Boundary Analysis: Identification of coupling issues, shared database anti-patterns, and boundary violations',
      'Data Flow Documentation: End-to-end data flow tracing across all service interactions with evidence at file and line level',
      'Integration Pattern Review: Assessment of API contracts, event-driven patterns, retry logic, and failure handling'
    ]
  },
  {
    title: 'Reliability & Resilience Review',
    icon: AlertTriangle,
    items: [
      'Error Handling Assessment: Systematic review of exception handling, dead-letter queues, and failure recovery across all services',
      'Circuit Breaker & Retry Audit: Identification of absent or misconfigured circuit breakers creating silent failure risks',
      'Queue Resilience Analysis: Redis, Kafka, and message queue depth, timeout, and drain-time risk assessment',
      'Race Condition Identification: Multi-writer database patterns, transaction coordination gaps, and consistency risks'
    ]
  },
  {
    title: 'Security & Compliance Assessment',
    icon: Shield,
    items: [
      'PHI / PII Data Flow Tracing: Systematic identification of sensitive data exposure across event payloads, APIs, and storage',
      'HIPAA Compliance Gap Analysis: Service-by-service assessment against HIPAA requirements with prioritised remediation',
      'CI/CD Security Review: Pipeline configuration assessment covering secrets management, access controls, and deployment gates',
      'Secrets & Credential Audit: Identification of hardcoded credentials, unrotated tokens, and insecure secret handling patterns'
    ]
  },
  {
    title: 'Observability & Performance Review',
    icon: Eye,
    items: [
      'Monitoring Coverage Assessment: Evaluation of Datadog, PagerDuty, and team-owned monitoring standards across services',
      'Distributed Tracing Gap Analysis: Assessment of end-to-end trace capability across service boundaries',
      'Performance Bottleneck Identification: Database query analysis, queue depth patterns, and peak load handling review',
      'Testing Strategy Assessment: Test coverage, CI/CD automation quality gates, and production-environment parity evaluation'
    ]
  }
];

const techStack = [
  {
    category: 'Languages & Frameworks',
    items: [
      { name: 'Ruby on Rails', logo: '/tech-logos/ruby.svg' },
      { name: 'Python (Django, FastAPI)', logo: '/tech-logos/python.svg' },
      { name: 'Node.js', logo: '/tech-logos/nodejs.svg' },
      { name: 'Java / Spring Boot', logo: '/tech-logos/java.svg' }
    ]
  },
  {
    category: 'Infrastructure Assessed',
    items: [
      { name: 'AWS (Lambda, ECS, RDS)', logo: '/tech-logos/aws.svg' },
      { name: 'Microsoft Azure', logo: '/tech-logos/azure-logo.svg' },
      { name: 'Kubernetes / VMware', logo: '/tech-logos/kubernetes.svg' },
      { name: 'On-Premises SQL Server', logo: '/tech-logos/sqlserver.png' }
    ]
  },
  {
    category: 'Observability Tools',
    items: [
      { name: 'Datadog', logo: '/tech-logos/datadog.svg' },
      { name: 'PagerDuty', logo: '/tech-logos/pagerduty.svg' },
      { name: 'CloudWatch', logo: '/tech-logos/aws.svg' },
      { name: 'Sentry', logo: '/tech-logos/sentry.svg' }
    ]
  },
  {
    category: 'CI/CD & Source Control',
    items: [
      { name: 'GitHub Actions', logo: '/tech-logos/github.svg' },
      { name: 'Jenkins', logo: '/tech-logos/jenkins.svg' },
      { name: 'GitLab CI/CD', logo: '/tech-logos/gitlab.svg' },
      { name: 'Terraform', logo: '/tech-logos/terraform.svg' }
    ]
  }
];

const industries = [
  {
    title: 'Healthcare & Health IT',
    icon: Shield,
    applications: [
      'HIPAA and PHI compliance gap assessment',
      'Prior authorization and claims processing platform audits',
      'EHR integration and data integrity review'
    ]
  },
  {
    title: 'Financial Services',
    icon: TrendingUp,
    applications: [
      'Payment processing reliability assessment',
      'API security and credential management review',
      'Regulatory compliance gap analysis'
    ]
  },
  {
    title: 'Technology & SaaS',
    icon: Search,
    applications: [
      'Pre-acquisition technical due diligence',
      'Pre-migration architecture assessment',
      'Production reliability review for scaling teams'
    ]
  },
  {
    title: 'Enterprise & Consulting',
    icon: Eye,
    applications: [
      'Platform consolidation readiness assessment',
      'Multi-team observability and monitoring standardisation',
      'Architecture documentation for undocumented legacy systems'
    ]
  }
];

const successStories = [
  {
    tag: 'Healthcare',
    title: 'Healthcare Technology Platform — Enterprise Microservices Audit',
    metrics: [
      '88 Ruby/Rails microservices fully audited and dependency-mapped in 8 weeks',
      'Active PHI exposure identified in event payload travelling to 4 subscribers — surfaced with file and line evidence',
      'ePAmotron retry gap and circuit breaker absence documented as critical reliability risks',
      'Complete PA lifecycle map produced with 19/20 steps code-confirmed',
      'Prioritised remediation roadmap delivered for reliability, security, and HIPAA compliance'
    ],
    link: '/case-studies/healthcare-audit'
  }
];

const methodology = [
  {
    title: 'Scope Definition & Access Setup',
    description: 'Engagement boundary agreed, codebase access provisioned, stakeholder workshops scheduled',
    icon: FileSearch
  },
  {
    title: 'Architecture Reconnaissance',
    description: 'Service inventory, dependency mapping, and domain structure documented from code, Confluence, and engineering interviews',
    icon: Search
  },
  {
    title: 'Deep Code Review',
    description: 'Systematic analysis of high-traffic and high-risk services across all 8 assessment areas with file and line-level evidence',
    icon: Eye
  },
  {
    title: 'Observability & Tooling Analysis',
    description: 'Datadog, PagerDuty, and CI/CD pipeline assessment against engineering best practices',
    icon: TrendingUp
  },
  {
    title: 'Risk Register Construction',
    description: 'Findings prioritised by severity (RED / AMBER / GREEN) with evidence-backed rationale for each',
    icon: AlertTriangle
  },
  {
    title: 'Remediation Roadmap Delivery',
    description: 'Quick wins and long-term initiatives structured by effort, impact, and dependency order',
    icon: CheckCircle2
  },
  {
    title: 'Stakeholder Presentation',
    description: 'Executive summary and engineering deep-dive presentations delivered to relevant audiences',
    icon: Shield
  }
];

const whyChooseUs = [
  {
    title: 'Evidence-First Methodology',
    description: 'Every finding is backed by specific code evidence at file and line level. We do not produce risk ratings based on assumptions, interviews alone, or pattern-matching without confirmation.',
    icon: Search
  },
  {
    title: 'Production System Experience',
    description: 'Our assessments are conducted by engineers who build and operate production systems at scale — not auditors working from checklists. We understand what the risks actually mean in live environments.',
    icon: TrendingUp
  },
  {
    title: 'Independent Perspective',
    description: 'As an external party with no stake in the existing architecture, we surface issues that internal teams normalise over time — the gaps that are genuinely invisible from inside.',
    icon: Eye
  },
  {
    title: 'Actionable, Not Academic',
    description: 'Our deliverables are remediation roadmaps, not audit reports. Every finding comes with a specific, prioritised recommendation that an engineering team can act on immediately.',
    icon: CheckCircle2
  }
];

const faqs = [
  {
    question: 'What is included in a standard audit engagement?',
    answer: 'A standard engagement covers service architecture mapping, reliability and error handling review, observability assessment, security and compliance gaps, CI/CD pipeline review, and a prioritised remediation roadmap. We scope coverage based on service count and timeline.'
  },
  {
    question: 'Do you write code fixes as part of the audit?',
    answer: 'No. Our audit engagements are assessment-only — we identify and document issues with specific evidence and recommendations. Remediation execution is a separate engagement if required.'
  },
  {
    question: 'How do you handle confidential codebases?',
    answer: 'Under NDA with strictly scoped access. We work within client-provisioned VPN or VDI environments, access only the repositories within the agreed scope, and produce no external copies of code or findings.'
  },
  {
    question: 'What size engineering organisations do you assess?',
    answer: 'We have assessed platforms with 88 microservices across 13 engineering teams. We scale scope and team size to match the engagement — smaller platforms can be assessed in as little as 2–4 weeks.'
  },
  {
    question: 'Can an audit be used for pre-acquisition due diligence?',
    answer: 'Yes. Our audit methodology and deliverables are structured to support technical due diligence for acquisition, investment, or merger scenarios — providing an independent, evidence-based view of platform risk and quality.'
  }
];

const AIAuditSolution = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <>
      <SEOHead
        title="AI Audit & Assessment Services | Agility.AI"
        description="Independent, evidence-based assessment of production AI systems, microservice architectures, and data platforms — identifying reliability gaps, compliance risks, and improvement roadmaps."
        keywords="AI audit, technical due diligence, microservices assessment, HIPAA compliance, security audit, reliability review, architecture assessment"
        url="https://www.agilitytech.ai/solutions/ai-audit"
      />
      <div className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80"
              alt="AI Audit Banner"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">AI & Application Audit Services</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                AI & Application Audit Services
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl">
                Independent, evidence-based assessment of production AI systems, microservice architectures,
                and data platforms — identifying reliability gaps, compliance risks, and improvement roadmaps
                before they become incidents.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium
                    hover:bg-teal-400 transition-all duration-300 inline-flex items-center space-x-2
                    shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <span>Schedule Your Assessment</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <button
                  className="bg-white/10 text-white px-8 py-4 rounded-lg text-lg font-medium
                    hover:bg-white/20 transition-all duration-300 inline-flex items-center space-x-2
                    border border-white/20 hover:border-white/30"
                >
                  <span>Download Audit Framework</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Key Metrics Section */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Assess Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              What We Assess
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8
                    border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                    transform hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center
                    justify-center mb-6">
                    <service.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{service.title}</h3>
                  <ul className="space-y-3">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-3 text-gray-300">
                        <CheckCircle2 className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                        <span className="text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology Stack Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Technology Stack
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {techStack.map((category, index) => (
                <div key={index} className="space-y-6">
                  <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">{category.category}</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {category.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center space-x-3 bg-gray-800/50 rounded-lg p-4
                          border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300"
                      >
                        <img
                          src={item.logo}
                          alt={`${item.name} logo`}
                          className="h-8 w-8 object-contain"
                        />
                        <span className="text-gray-300 font-medium">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industry Applications Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Industry Applications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {industries.map((industry, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8
                    border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                    transform hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center
                    justify-center mb-6">
                    <industry.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{industry.title}</h3>
                  <ul className="space-y-3">
                    {industry.applications.map((app, idx) => (
                      <li key={idx} className="flex items-start space-x-3 text-gray-300">
                        <ArrowRight className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                        <span className="text-sm leading-relaxed">{app}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Success Stories Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Featured Success Stories
            </h2>
            <div className="max-w-2xl mx-auto">
              {successStories.map((story, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                    hover:bg-gray-800/70 transition-all duration-300"
                >
                  <div className="text-sm text-teal-400 font-semibold mb-4">{story.tag}</div>
                  <h3 className="text-xl font-bold text-white mb-6">{story.title}</h3>
                  <div className="space-y-4 mb-6">
                    {story.metrics.map((metric, idx) => (
                      <div key={idx} className="flex items-center space-x-3 text-gray-300">
                        <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                        <span className="text-sm">{metric}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to={story.link}
                    className="inline-flex items-center text-teal-400 hover:text-teal-300
                      transition-colors font-medium"
                  >
                    <span>Read Full Case Study</span>
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Methodology Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Our Methodology
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {methodology.map((step, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6
                    border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-8 h-8 bg-teal-500/10 rounded-full flex items-center
                      justify-center text-teal-400 font-bold">
                      {index + 1}
                    </div>
                    <div className="w-8 h-8 bg-teal-500/10 rounded-lg flex items-center justify-center">
                      <step.icon className="h-5 w-5 text-teal-400" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Agility.AI Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Why Choose Agility.AI
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs.map((reason, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                    hover:bg-gray-800/70 transition-all duration-300 text-center"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center
                    justify-center mb-6 mx-auto">
                    <reason.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{reason.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <HelpCircle className="h-12 w-12 text-teal-400 mx-auto mb-4" />
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-xl text-gray-400">
                  Everything you need to know about our AI audit and assessment process
                </p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-900/70 transition-all duration-300"
                    >
                      <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                      <ChevronDown
                        className={`h-5 w-5 text-teal-400 flex-shrink-0 transition-transform duration-300 ${
                          openFaqIndex === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-5">
                        <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileSearch className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready for an Independent Assessment?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Schedule an assessment with our audit specialists to get an evidence-based view of your
                platform's reliability, security, and compliance posture.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4
                  rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                  shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Schedule Your Assessment</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AIAuditSolution;
